import { Repeat } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { EchoflicksVideoSession } from "../types";
import {
  resolveEchoAttribution,
  type EchoAttribution,
  type EchoRecurrence,
} from "./attribution";
import { isStudioFilm, studioAgencyName } from "./studioFilms";
import { useEchoContact } from "./useEchoContact";
import { useEchoReferral } from "./useEchoReferral";
import { useStudioDirectory } from "./useStudioDirectory";

/**
 * The hero badges for an echo: recurring-user + this-echo-position + attribution
 * source. Pure/presentational — all resolution happens in `attribution.ts`.
 *
 * Loading rule (plan §7 G4): never flash "Organic / shared" while the contact is
 * still loading. An organic result during load is ambiguous (the contact might
 * turn out to be ctwa/website), so we show a neutral placeholder instead. A
 * Snap/other channel comes from the echo itself and renders immediately.
 */
export const EchoAttributionBadges = ({
  attribution,
  recurrence,
  contactLoading,
  referralLoading = false,
}: {
  attribution: EchoAttribution;
  recurrence: EchoRecurrence;
  contactLoading: boolean;
  referralLoading?: boolean;
}) => {
  const sourceIsPending =
    referralLoading || (contactLoading && attribution.channel === "organic");

  return (
    <>
      {recurrence.isRecurringUser ? (
        <Badge
          variant="secondary"
          title={
            recurrence.deliveredCount != null
              ? `${recurrence.deliveredCount} echoes delivered`
              : undefined
          }
        >
          <Repeat className="size-3" />
          Recurring user
        </Badge>
      ) : null}

      {recurrence.isThisEchoRepeat !== null ? (
        <Badge variant="outline">
          {recurrence.isThisEchoRepeat ? "Repeat echo" : "1st echo"}
        </Badge>
      ) : null}

      {sourceIsPending ? (
        <Badge
          variant="outline"
          className="animate-pulse text-muted-foreground"
          aria-hidden
        >
          Source…
        </Badge>
      ) : (
        <Badge
          variant="outline"
          title={attribution.detail ?? undefined}
          aria-label={`Source: ${attribution.label}`}
        >
          {attribution.emoji ? `${attribution.emoji} ` : ""}
          {attribution.label}
        </Badge>
      )}
    </>
  );
};

/**
 * Self-contained source-only chip for the telemetry section (generalizes the
 * old snap-only badge to every channel). Fetches its own contact — React Query
 * dedupes with the hero's fetch. Renders nothing while an organic result is still
 * ambiguous during load (G4).
 */
export const EchoSourceChip = ({
  record,
}: {
  record: EchoflicksVideoSession;
}) => {
  const { contact, isLoading } = useEchoContact(record);
  const { referral, isLoading: referralLoading } = useEchoReferral(record);
  const directory = useStudioDirectory({ enabled: isStudioFilm(record) });
  const attribution = resolveEchoAttribution(record, contact, referral, {
    agencyName: studioAgencyName(record, directory.agenciesById),
  });
  if (referralLoading || (isLoading && attribution.channel === "organic")) {
    return null;
  }
  return (
    <Badge
      variant="outline"
      title={attribution.detail ?? undefined}
      aria-label={`Source: ${attribution.label}`}
    >
      {attribution.emoji ? `${attribution.emoji} ` : ""}
      {attribution.label}
    </Badge>
  );
};
